/**
 * Trade commands (spec §7.3 Market, §9.1; plan §6 M2-07): pure `CmdResult` commands for the Market screen. Each
 * prices through `quote()` at the career's game time with the player's standing toward the station's faction,
 * and returns the new career and the new market state (saturation). Sales add to `stats.earned`.
 *
 * Refusals: `serviceUnavailable` (no market), `invalidAmount`, `notInCargo`, `notBoughtHere` (the station does not
 * buy it; tetherstone sells only at the Refinery; sealed contract pods never sell), `notSoldHere`, `cargoFull`,
 * `insufficientFunds`.
 *
 * **M4 (plan I3, spec §5.6):** under the strategic economy the career runtime commits a ware trade through the worker
 * first, then runs `sellWare`/`buyWare` with the worker's executed unit price (`executedUnit`): the total is
 * `executedUnit × tonnes` in whole credits and no saturation is written (the station's real stock moved instead).
 *
 * **M7 §12.1 logging:** every tetherstone sale is appended to the Career's bounded sale log and running totals, and
 * every sale books its credits to the Career's income mix (`story/logging.ts`: ores → `wareSaleSource`, components
 * → `componentSaleSource`). While the strategic prices are active, component sales write no saturation either (spec §5.7).
 *
 * **M7 S14 retaliation refusals (spec §9):** a landed Tethys retaliation row refuses sales and purchases at the
 * stations it names (`story/refusals.ts`): every Tethys service under `dockingRefused`, ore sales at the Refinery
 * under `refinerySalesRefused`, component sales under `componentsRefused`. The runtime pre-checks `marketRefusal`
 * before committing a trade through the strategic worker; the check here is the backstop.
 */
import { stationDef } from '../data/m2'
import { WARE_IDS, wareIndex, type M2Data, type StationDef, type StationId, type WareId } from '../data/m2Schemas'
import { EPSILON, cargoFreeT, contentsLoaded, withWallet, withWare, withoutComponent } from '../career/ops'
import { wareHoldT } from '../career/hullFit'
import { cargoFull, fail, formatTonnes, insufficientFunds, invalidAmount, ok, type CmdFailure, type CmdResult } from '../career/result'
import type { CargoComponent, Career } from '../career/types'
import { LOGGING, recordIncome, recordTetherSale } from '../story/logging'
import { marketRefusal } from '../story/refusals'
import type { MarketState } from '../space/contracts'
import { withPurchase, withSale } from './market'
import { quote, quoteKey, strategicPricesActive, type QuoteItem } from './quote'

export interface TradeValue {
  career: Career
  market: MarketState
  /** Credits received (sales) or paid (purchases). */
  credits: number
}

/** A ware's player-facing label (tetherstone keeps its unidentified name: the Market screen may reveal it). */
function wareLabel(data: M2Data, ware: WareId): string {
  const def = data.wares.wares[wareIndex(ware)]
  return def.unidentifiedName ?? def.name
}

function marketStation(data: M2Data, stationId: StationId): StationDef | CmdFailure {
  const station = stationDef(data, stationId)
  if (!station.services.includes('market')) {
    return fail('serviceUnavailable', `${station.name} has no market.`, { station: station.id })
  }
  return station
}

function standingAt(career: Career, station: StationDef): number {
  return career.standings[station.faction]
}

function withEarned(career: Career, credits: number): Career {
  return { ...career, stats: { ...career.stats, earned: career.stats.earned + credits } }
}

/**
 * A ware sale's Career (M7 §12.1 logging): the credits go to the income mix, and a tetherstone sale is appended to
 * the Career's sale log and totals.
 */
function withSaleLog(career: Career, stationId: StationId, ware: WareId, tonnes: number, credits: number): Career {
  const booked = recordIncome(career, LOGGING.wareSaleSource, credits)
  if (ware !== 'tetherstone') return booked
  return recordTetherSale(booked, { stationId, tonnes, credits })
}

function validTonnes(tonnes: number): boolean {
  return Number.isFinite(tonnes) && tonnes > 0
}

/** Is this component something the player can sell on its own? Sealed contract cargo and loaded pods cannot. */
function sellRefusal(component: CargoComponent): CmdFailure | null {
  if (component.sealed === true) {
    return fail('notBoughtHere', 'Sealed contract cargo cannot be sold.', { id: component.id })
  }
  const contents = component.contents
  if (contents !== undefined && contentsLoaded(contents)) {
    return fail('notBoughtHere', 'The pod still holds cargo: unload it before selling the pod.', { id: component.id })
  }
  return null
}

/**
 * Sells `tonnes` of `ware` from the hold at `stationId`. `executedUnit` (M4): the unit price the strategic worker
 * executed at; the sale is then booked at that price and writes no saturation.
 */
export function sellWare(
  career: Career,
  data: M2Data,
  market: MarketState,
  stationId: StationId,
  ware: WareId,
  tonnes: number,
  executedUnit?: number,
): CmdResult<TradeValue> {
  const station = marketStation(data, stationId)
  if ('ok' in station) return station
  const refused = marketRefusal(career, station, 'sellWare')
  if (refused !== null) return refused
  if (!validTonnes(tonnes)) return invalidAmount('tonnage', tonnes)
  const held = career.cargo.wares[ware]
  if (tonnes > held + EPSILON) {
    return fail('notInCargo', `Only ${formatTonnes(held)} t of ${wareLabel(data, ware)} in the hold.`, { ware, held, requested: tonnes })
  }
  const qty = tonnes < held ? tonnes : held
  if (executedUnit !== undefined) {
    const credits = Math.round(executedUnit * qty)
    const booked = withEarned(withWallet(withWare(career, ware, -qty), credits), credits)
    return ok({ career: withSaleLog(booked, station.id, ware, qty, credits), market, credits })
  }
  const q = quote(data, market, station.id, { kind: 'ware', ware }, qty, 'sell', career.gameTime, standingAt(career, station))
  if (q === null) {
    const message =
      ware === 'tetherstone'
        ? `${station.name} will not touch the ${wareLabel(data, ware).toLowerCase()}. Only the Refinery buys it.`
        : `${station.name} does not buy ${wareLabel(data, ware)}.`
    return fail('notBoughtHere', message, { station: station.id, ware })
  }
  const next = withSaleLog(withEarned(withWallet(withWare(career, ware, -qty), q.total), q.total), station.id, ware, qty, q.total)
  const cfg = data.wares.market.saturation
  const nextMarket = withSale(market, station.id, quoteKey({ kind: 'ware', ware }), qty, station.capacityT, career.gameTime, cfg.halfLifeSeconds)
  return ok({ career: next, market: nextMarket, credits: q.total })
}

/**
 * Buys `tonnes` of `ware` from `stationId` into the hold. `executedUnit` (M4): the unit price the strategic worker
 * executed at; the purchase is then booked at that price and writes no saturation.
 */
export function buyWare(
  career: Career,
  data: M2Data,
  market: MarketState,
  stationId: StationId,
  ware: WareId,
  tonnes: number,
  executedUnit?: number,
): CmdResult<TradeValue> {
  const station = marketStation(data, stationId)
  if ('ok' in station) return station
  const refused = marketRefusal(career, station, 'buyWare')
  if (refused !== null) return refused
  if (!validTonnes(tonnes)) return invalidAmount('tonnage', tonnes)
  if (executedUnit !== undefined) {
    const credits = Math.round(executedUnit * tonnes)
    const room = cargoFreeT(career)
    // M10 P5a6b H2: the buy takes its class-weighted hold tonnes (raw tonnes on every shipped hull).
    const holdT = wareHoldT(career.ship.defId, ware, tonnes)
    if (holdT > room + EPSILON) return cargoFull(holdT, room)
    if (credits > career.wallet) return insufficientFunds(credits, career.wallet)
    return ok({ career: withWallet(withWare(career, ware, tonnes), -credits), market, credits })
  }
  const q = quote(data, market, station.id, { kind: 'ware', ware }, tonnes, 'buy', career.gameTime, standingAt(career, station))
  if (q === null) return fail('notSoldHere', `${station.name} does not sell ${wareLabel(data, ware)}.`, { station: station.id, ware })
  const free = cargoFreeT(career)
  const holdT = wareHoldT(career.ship.defId, ware, tonnes)
  if (holdT > free + EPSILON) return cargoFull(holdT, free)
  if (q.total > career.wallet) return insufficientFunds(q.total, career.wallet)
  const next = withWallet(withWare(career, ware, tonnes), -q.total)
  const cfg = data.wares.market.saturation
  const nextMarket = withPurchase(market, station.id, quoteKey({ kind: 'ware', ware }), tonnes, station.capacityT, career.gameTime, cfg.halfLifeSeconds)
  return ok({ career: next, market: nextMarket, credits: q.total })
}

/** Sells the component `componentId` from the hold at `stationId`. */
export function sellComponent(
  career: Career,
  data: M2Data,
  market: MarketState,
  stationId: StationId,
  componentId: string,
): CmdResult<TradeValue> {
  const station = marketStation(data, stationId)
  if ('ok' in station) return station
  const refused = marketRefusal(career, station, 'sellComponent')
  if (refused !== null) return refused
  const component = career.cargo.components.find((c) => c.id === componentId)
  if (component === undefined) return fail('notInCargo', 'That component is not in the hold.', { id: componentId })
  const refusal = sellRefusal(component)
  if (refusal !== null) return refusal
  const item: QuoteItem = { kind: 'component', component }
  const q = quote(data, market, station.id, item, 1, 'sell', career.gameTime, standingAt(career, station))
  if (q === null) {
    const what = component.type === 'dataCore' ? 'data cores' : 'salvaged components'
    return fail('notBoughtHere', `${station.name} does not buy ${what}.`, { station: station.id, id: component.id })
  }
  const sold = withEarned(withWallet(withoutComponent(career, component.id), q.total), q.total)
  const next = recordIncome(sold, LOGGING.componentSaleSource, q.total)
  if (strategicPricesActive()) return ok({ career: next, market, credits: q.total })
  const cfg = data.wares.market.saturation
  const nextMarket = withSale(market, station.id, quoteKey(item), component.massT, station.capacityT, career.gameTime, cfg.halfLifeSeconds)
  return ok({ career: next, market: nextMarket, credits: q.total })
}

/**
 * Sells everything in the hold that `stationId` buys: every ware it prices, then every loose component it buys.
 * Contract components (recovery targets, sealed pods) and cargo pods stay aboard. Each sale moves the saturation
 * before the next one is priced. Refuses with `notBoughtHere` when nothing in the hold sells here.
 */
export function sellAllCargo(career: Career, data: M2Data, market: MarketState, stationId: StationId): CmdResult<TradeValue> {
  const station = marketStation(data, stationId)
  if ('ok' in station) return station
  const waresRefused = marketRefusal(career, station, 'sellWare')
  if (waresRefused !== null && marketRefusal(career, station, 'sellComponent') !== null) return waresRefused
  let c = career
  let m = market
  let credits = 0
  let sold = 0
  for (const ware of WARE_IDS) {
    if (c.cargo.wares[ware] <= EPSILON || station.prices.wares[ware] === undefined) continue
    const r = sellWare(c, data, m, station.id, ware, c.cargo.wares[ware])
    if (!r.ok) continue
    c = r.value.career
    m = r.value.market
    credits += r.value.credits
    sold++
  }
  for (const component of career.cargo.components) {
    if (component.contractId !== undefined || component.type === 'cargoPod') continue
    const r = sellComponent(c, data, m, station.id, component.id)
    if (!r.ok) continue
    c = r.value.career
    m = r.value.market
    credits += r.value.credits
    sold++
  }
  if (sold === 0) return fail('notBoughtHere', `Nothing in the hold sells at ${station.name}.`, { station: station.id })
  return ok({ career: c, market: m, credits })
}
